import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - functions
import { WEB_SERVICE_MIMS } from "../../config/constants";
import { isProtocolWithDetails } from "../../config/functions";
// Components
import { IndicatorDataDiv, IndicatorDataText, SessionHeaderMainDiv, SessionHeaderDiv, SessionTitle, SessionHideDiv } from "./styles";
import Loader from "../loader";
import Axios from "../../config/axios";
import PatientNavigation from "../patientNavigation";
import Title from "../title";
import DownloadFiles from "./sections/downloadFiles";
import SessionData from "./sections/sessionData";
import ProtocolNavigation from "./sections/protocolNavigation";
import { RenderProtocolData } from "./sections/renderProtocolData";
import GraphicContainer from "../GraphicContainer";
// Plots
import ScorePlot from "./sections/scorePlot";
import RestPerformancePlot from "./sections/restPerformance";
import SuccessRatesPlot from "./sections/successRates";
import FrequencyPlot from "./sections/frequency";
import HandTrackingPlot from "./sections/handTracking";
import DifficultyPlot from "./sections/difficulty";
import CompletionTimePlot from "./sections/completionTime";

const Reports = () => {
  // Set protocol info
  const [protocolTitle, setProtocolTitle] = useState("");
  // Recordings
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [recordings, setRecordings] = useState([]);
  // Set plot images
  const scorePlotRef = useRef(null);
  const restPerformanceRef = useRef(null);
  const successRatesRef = useRef(null);
  const frequencyRef = useRef(null);
  const handTrackingRef = useRef(null);
  const difficultyRef = useRef(null);
  const completionTimesRef = useRef(null);

  const [toggleRechargeReports, setToggleRechargeReports] = useState(false);
  const { patientId, protocolId, software } = useParams();
  const [t] = useTranslation("global");
  const storeLng = useSelector(({ language }) => language.lng);

  // Fetch Recordings
  useEffect(() => {
    Axios("get", `${WEB_SERVICE_MIMS}/patients/reports/protocol/get/${patientId}/${protocolId}/${software}`)
      .then(({ data }) => {
        setRecordings(data);
        setIsLoadingReports(false);
      })
      .catch((error) => {
        console.log("Get protocol recordings: ", error);
      });

    return () => {
      setRecordings([]);
      setIsLoadingReports(true);
    };
  }, [patientId, protocolId, software, toggleRechargeReports]);

  // Set protocol info
  useEffect(() => {
    RenderProtocolData(protocolId, t, setProtocolTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolId, storeLng]);

  const renderDownloadFiles = () => {
    const plotsRef = {
      scorePlotRef,
      restPerformanceRef,
      successRatesRef,
      frequencyRef,
      handTrackingRef,
      difficultyRef,
      completionTimesRef,
    };

    return <DownloadFiles sessionsData={recordings} plotsRef={plotsRef} />;
  };

  const renderScorePlot = () => {
    const scoreRecordings = recordings.filter((recording) => recording.hasOwnProperty("score"));
    if (scoreRecordings.length) {
      return (
        <GraphicContainer ref={scorePlotRef}>
          <ScorePlot recordings={recordings} />
        </GraphicContainer>
      );
    }
  };

  const renderRestPerformancePlot = () => {
    const restRecordings = recordings.filter((recording) => recording.hasOwnProperty("restPerformance"));
    if (restRecordings.length) {
      return (
        <GraphicContainer ref={restPerformanceRef}>
          <RestPerformancePlot recordings={recordings} />
        </GraphicContainer>
      );
    }
  };

  const renderSuccessRatesPlot = () => {
    const successRatesRecordings = recordings.filter((recording) => recording.hasOwnProperty("successRates"));
    if (successRatesRecordings.length) {
      return (
        <GraphicContainer ref={successRatesRef}>
          <SuccessRatesPlot recordings={recordings} />
        </GraphicContainer>
      );
    }
  };

  const renderFrequencyPlot = () => {
    const frequencyRecordings = recordings.filter((recording) => recording.hasOwnProperty("frequencies"));
    if (frequencyRecordings.length) return <FrequencyPlot ref={frequencyRef} recordings={recordings} />;
  };

  const renderHandTrackingPlot = () => {
    const handTrackingRecordings = recordings.filter((recording) => recording.hasOwnProperty("handTrackingState"));
    if (handTrackingRecordings.length) return <HandTrackingPlot ref={handTrackingRef} recordings={recordings} />;
  };

  const renderDifficultyPlot = () => {
    const difficultyRecordings = recordings.filter((recording) => recording.hasOwnProperty("difficultyLevel"));
    if (difficultyRecordings.length) return <DifficultyPlot ref={difficultyRef} recordings={recordings} />;
  };

  const renderCompletionTimesPlot = () => {
    const completionTimesRecordings = recordings.filter((recording) => recording.hasOwnProperty("completionTimes"));
    if (completionTimesRecordings.length) {
      return (
        <GraphicContainer ref={completionTimesRef}>
          <CompletionTimePlot recordings={recordings} />
        </GraphicContainer>
      );
    }
  };

  const renderHeaderTable = () => {
    if (Boolean(recordings.length)) {
      return (
        <>
          <Title title={t("reports.sessionsList.title")} marginBottom="10px" />
          <SessionHeaderMainDiv>
            <SessionHeaderDiv>
              <SessionTitle width="100px" width910="75px">
                {t("reports.sessionsList.tableSessionText")}
              </SessionTitle>
              <SessionTitle width="138px" width910="80px">
                {t("reports.sessionsList.tableDateText")}
              </SessionTitle>
              <SessionTitle width="108px" width910="80px">
                {t("reports.sessionsList.tableHourText")}
              </SessionTitle>
              <SessionHideDiv>
                {!isProtocolWithDetails(protocolId) && (
                  <SessionTitle width="120px">{t("reports.sessionsList.tableScoreText")}</SessionTitle>
                )}
                {isProtocolWithDetails(protocolId) && <SessionTitle width="90px">{t("reports.sessionsList.tableDetail")}</SessionTitle>}
              </SessionHideDiv>
            </SessionHeaderDiv>
          </SessionHeaderMainDiv>
        </>
      );
    }
  };

  const renderTotalSessions = () => {
    const sortedSessions = [...recordings].sort((a, b) => {
      return new Date(b.STARTING_DATE) - new Date(a.STARTING_DATE);
    });

    return sortedSessions.map((session, i) => {
      const sessionIndex = recordings.length - i;
      const sessionId = session.SESSION_ID;
      const date = moment(session.STARTING_DATE).format("DD/MM/YYYY");
      const hour = moment(session.STARTING_DATE).format("HH:mm:ss");

      return (
        <SessionData
          key={sessionId}
          sessionId={sessionId}
          sessionNumber={sessionIndex}
          date={date}
          hour={hour}
          score={session.score ? session.score : 0}
          software={software}
          toggleRechargeReports={toggleRechargeReports}
          setToggleRechargeReports={setToggleRechargeReports}
          setIsLoadingReports={setIsLoadingReports}
        />
      );
    });
  };

  return (
    <>
      {isLoadingReports ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={software} />
          <ProtocolNavigation />
          <Title title={protocolTitle} marginBottom="10px" />
          {!recordings.length ? (
            <IndicatorDataDiv>
              <IndicatorDataText>{t("reports.sessionsRecordedMessage")}</IndicatorDataText>
            </IndicatorDataDiv>
          ) : (
            <>
              {renderDownloadFiles()}
              {renderScorePlot()}
              {renderRestPerformancePlot()}
              {renderSuccessRatesPlot()}
              {renderFrequencyPlot()}
              {renderHandTrackingPlot()}
              {renderDifficultyPlot()}
              {renderCompletionTimesPlot()}
              {renderHeaderTable()}
              {renderTotalSessions()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default Reports;
